import { AfterViewInit, Component, Input, OnInit, ViewChild, ViewEncapsulation, OnDestroy, ViewChildren, QueryList } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbTabset } from '@ng-bootstrap/ng-bootstrap';
import { GridComponent, RowDeselectEventArgs, RowSelectEventArgs } from '@syncfusion/ej2-angular-grids';
import { catchError } from 'rxjs/operators';
import { IApiTypedResult, Order, OrderUser } from '../../shared/models/index';
import { OrderService, StorageManagerService, AuthorizationService, SettingsService } from '../../shared/services/index';
import { Utilities } from '../../shared/services/utilities';
import { Subscription } from 'rxjs';
import { IApiResult } from '../../shared/models/api-result';
import { ToastrService } from 'ngx-toastr';
import { DomSanitizer } from '@angular/platform-browser';
import { TooltipComponent, DialogComponent } from '@syncfusion/ej2-angular-popups';
import { DeleteButtonComponent } from '../common/delete-button/delete-button.component';
import { ClickEventArgs } from '@syncfusion/ej2-angular-navigations/src';
import { EmitType } from '@syncfusion/ej2-base';
import { fadeInOut } from '../../shared/helpers/animations';
import { GridPreferencesService } from '../../shared/services/grid-preferences.service';

@Component({
    selector: 'order-tracker',
    templateUrl: './order-tracker.component.html',
    styleUrls: ['./order-tracker.component.scss'],
    encapsulation: ViewEncapsulation.Emulated,
    animations: [fadeInOut],
})
export class OrderTrackerComponent implements OnInit, AfterViewInit, OnDestroy {

  showWelcomeMessage: boolean;
  sub: Subscription;


    // Angular form that contains controls
    orderTrackerForm: UntypedFormGroup;

    @ViewChild('tabset', { static: true })
    public tabset: NgbTabset;

    @ViewChild('orderTrackerAllGrid')
    public orderTrackerAllGrid: GridComponent;

    @Input() orderTrackerAllGridData: any[];

    @ViewChild('orderTrackerDraftGrid')
    public orderTrackerDraftGrid: GridComponent;

    @ViewChild('deleteOrderDraftButton')
    public deleteButtonDrafts: DeleteButtonComponent;

    @ViewChildren(TooltipComponent) toolTips: QueryList<TooltipComponent>;
    public tooltipObj: TooltipComponent;

    @Input() orderTrackerDraftGridData: any[];


    // Modal properties
    @ViewChild('orderTrackerModal')
    ejDialogOrderTracker: DialogComponent;

    position: object = { X: 'center', Y: 'center' };
    modalWidth = '32%';
    modalHeight = 'auto';
    animationSettings: Object = { effect: 'Zoom' };
    isModal = true;
    orderTrackerDialogVisible: boolean;

    // Modal template variables
    currentOrderStatus: string = '';
    currentSelectedToolbarAction = {
        isAcceptDelivery: false,
        isResubmitOrder: false,
        isRequestOrderCancellation: false
    }

    // Common to both grids
    public orderTrackerGridInitialPage: Object;
    public orderTrackerGridFormatOptions: object;
    public orderTrackerGridToolbarOptions: Object;

    // What actions are allowed for which statuses
    public whichStatusesAllowAcceptDelivery: string[];
    public whichStatusesAllowResubmit: string[];
    public whichStatusesAllowCancel: string[];
    public whichStatusesAllowDelete: string[];

    // Controls button enabled states for the two grids
    canAcceptDelivery = {
        all: false,
        draft: false,
        isAllowedOnGrid: true,
        isAllowedOnDraftGrid: false
    };
    canResubmit = {
        all: false,
        draft: false,
        isAllowedOnGrid: true,
        isAllowedOnDraftGrid: false
    };
    canCancel = {
        all: false,
        draft: false,
        isAllowedOnGrid: true,
        isAllowedOnDraftGrid: false
    };
    canDelete = {
        all: false,
        draft: false,
        isAllowedOnGrid: false,
        isAllowedOnDraftGrid: true
    };

    // Can user create new orders
    canCreate: boolean;
    canDeleteDraft: boolean;

    canOrderAdmin: boolean;
    canOrderResubmit: boolean;
    canCancelOrder: boolean;

    selectedOrderRevisionId = {
        all: null as number,
        draft: null as number
    };

    pageState: string;
    errorMessage: string;
    activeTab: string;
    public clipmodecolumn = { class: 'e-tooltipcolumn' };

    dateRangeDataSource = ['90 days', '180 days', '1 Year', '2 Years', 'All'];
    selectedDateRangeFilter = '1 Year';

    // Messages
    ORDER_RESUBMIT_MESSAGE = `You have elected to resubmit this order for additional processing.&nbsp;We will copy the contents of this order and create a new one so you can update and submit to Reed Tech for processing.&nbsp;We will notify you when your order has been saved to the Drafts tab.`;
    ordersInProcess: any = {
        resubmit: [],
        acceptDelivery: [],
        cancel: []
    };

    disabledColumns: string[] = ['Order ID', 'Order Name', 'Order Status'];

    constructor(private fb: UntypedFormBuilder,
        private activatedRoute: ActivatedRoute,
        public orderService: OrderService,
        private router: Router, private toastr: ToastrService,
        private storageManagerService: StorageManagerService,
        private authorizationService: AuthorizationService,
        public utilService: Utilities,
        private sanitizer: DomSanitizer,
        private settingsService: SettingsService,
        public gridPreferencesService: GridPreferencesService,
    ) { }


    ngOnInit() {
      // listen for the status change to determine if we need to show the welcome message

      if (!this.storageManagerService.read('WelcomeShown')) {
        this.showWelcomeMessage = true;
        this.storageManagerService.write('WelcomeShown', true);
      }
      else {
        this.showWelcomeMessage = false;
      }
      this.loadInitialToolbar();
      this.orderTrackerGridInitialPage = { currentPage: 1, pageSize: 25, pageSizes: [25, 50, 75, 100, 'All'] };
      this.orderTrackerGridFormatOptions = { type: 'dateTime', format: Utilities.getLocaleDateString() };

        // Say which statuses are required for which actions
        this.whichStatusesAllowAcceptDelivery = [
            this.orderService.ORDER_STATUS_ORDER_DELIVERED_AWAITING_APPROVAL
        ];
        this.whichStatusesAllowResubmit = [
            this.orderService.ORDER_STATUS_ORDER_DELIVERED_AWAITING_APPROVAL
        ];
        this.whichStatusesAllowCancel = [
            this.orderService.ORDER_STATUS_IN_PROCESS,
            this.orderService.ORDER_STATUS_PENDING
        ];
        this.whichStatusesAllowDelete = [
            this.orderService.ORDER_STATUS_DRAFT
        ];

        // Tell angular form what fields it has.
        this.orderTrackerForm = this.fb.group({
            orderTrackerAllGrid: [''],
            orderTrackerDraftGrid: ['']
        });

      // set focus on the invisible div so the first tab will bring it to the "Skip to Main Navigation" button because it must be the first tab target on the page (508 Compliance)
      this.initTabIndex()

      this.loadData();

      this.activeTab = 'AllOrders';
  }

  initTabIndex() {
    (document.activeElement as HTMLElement).blur();

    const main = document.getElementById("firstDiv");

    if (main) {
      main.focus();
    }
  }

  hideDialog: EmitType<object> = () => {
    this.ejDialogOrderTracker.hide();
  }

  orderTrackerDialogButtons: Object[] = [{
    click: this.hideDialog.bind(this),
    buttonModel:{
        content: 'OK',
        isPrimary: true
    }
  }]

  loadInitialToolbar() {
    this.canOrderAdmin = this.authorizationService.hasPermission(this.orderService.PERMISSION_ORDER_ADMIN);

    this.canOrderResubmit = this.authorizationService.hasPermission(this.orderService.PERMISSION_ORDER_ADMIN)
      || this.authorizationService.hasPermission(this.orderService.PERMISSION_CAN_WRITE_ORDER);

    this.canCancelOrder = this.authorizationService.hasPermission(this.orderService.PERMISSION_CAN_CANCEL_ORDER);

    // Allowed to make new orders?
    this.canCreate = this.authorizationService.hasPermission(this.orderService.PERMISSION_CAN_WRITE_ORDER);

    this.canDeleteDraft = this.authorizationService.hasPermission(this.orderService.PERMISSION_CAN_DELETE_DRAFT_ORDER);

    this.canResubmit.isAllowedOnGrid = this.canOrderResubmit;
    this.canAcceptDelivery.isAllowedOnGrid = this.canOrderResubmit;
    this.canCancel.isAllowedOnGrid = this.canCancelOrder;

    this.orderTrackerGridToolbarOptions = this.createCustomToolbarItems(this.canCancel && this.canDelete.all,
      this.canDelete.isAllowedOnGrid,
      this.canAcceptDelivery.isAllowedOnGrid,
      this.canResubmit.isAllowedOnGrid,
      this.canCancel.isAllowedOnGrid,
      this.canCreate);
  }

  tabChanged(event) {
    this.activeTab = event.nextId;

    this.currentOrderStatus = '';
    this.resetCurrentSelectedToolbarAction();

    if(this.activeTab === 'DraftOrders'){
        this.canDelete['draft'] = (this.whichStatusesAllowDelete as any).includes('Draft');
        this.orderTrackerGridToolbarOptions = this.createCustomToolbarItems(this.canCancel && this.canDelete.draft && this.canDeleteDraft,
                                                                            this.canDelete.isAllowedOnDraftGrid,
                                                                            this.canAcceptDelivery.isAllowedOnDraftGrid,
                                                                            this.canResubmit.isAllowedOnDraftGrid,
                                                                            this.canCancel.isAllowedOnDraftGrid,
                                                                            this.canCreate);
    }
    else{
        this.orderTrackerGridToolbarOptions = this.createCustomToolbarItems(this.canCancel && this.canDelete.all,
                                                                            this.canDelete.isAllowedOnGrid,
                                                                            this.canAcceptDelivery.isAllowedOnGrid,
                                                                            this.canResubmit.isAllowedOnGrid,
                                                                            this.canCancel.isAllowedOnGrid,
                                                                            this.canCreate);
    }

  }

  createCustomToolbarItems(deletePermission : boolean, isDeleteAllowedOnTab: boolean, isAcceptDeliveryAllowedOnTab: boolean,
                           isResubmitOrderAllowedOnTab: boolean, isRequestOrderCancellationAllowedOnTab: boolean, isCreateNewOrderAllowed: boolean){
    let toolbarItems = [];

    if (isCreateNewOrderAllowed) {
        toolbarItems.push(
          {
            text: 'Create New Order',
            tooltipText: 'Add a new order form to this list',
            prefixIcon: 'fas fa-plus',
            id: 'create-form-btn'
          }
        );
    }

    if(isAcceptDeliveryAllowedOnTab){
        toolbarItems.push(
            {
                text: 'Accept Delivery',
                tooltipText: 'Accept delivery of the selected order',
                prefixIcon: 'fas fa-check',
                id: 'tool-bar-accept-delivery-btn'
            }
        );
    }

    if(isResubmitOrderAllowedOnTab){
        toolbarItems.push(
            {
                text: 'Resubmit Order',
                tooltipText: 'Resubmit the selected order',
                prefixIcon: 'fas fa-rotate-right',
                id: 'tool-bar-resubmit-order'
            }
        );
    }

    if(isRequestOrderCancellationAllowedOnTab){
        toolbarItems.push(
            {
                text: 'Request Order Cancellation',
                tooltipText: 'Request cancellation of the selected order',
                prefixIcon: 'fas fa-xmark',
                id: 'tool-bar-request-order-cancellation'
            }
        );
    }

    if(deletePermission && isDeleteAllowedOnTab){
        toolbarItems.push(
            {
                text: 'Delete Order',
                tooltipText: 'Delete the selected draft order',
                prefixIcon: 'fas fa-trash',
                id: 'tool-bar-delete'
            }
        )
    }

    toolbarItems.push('Search');
    toolbarItems.push('ColumnChooser');

    return toolbarItems;
  }

  toolbarClick(args: ClickEventArgs): void {

    this.resetCurrentSelectedToolbarAction();

    if (args.item.id === 'create-form-btn' && this.canCreate) {
        this.router.navigate(["/order"]);
    }

    // Order Draft Grid Buttons
    if(this.orderTrackerDraftGrid && this.canDeleteDraft){
        if(args.item.id === 'tool-bar-delete' && this.selectedOrderRevisionId.draft){
            this.deleteButtonDrafts.revisionId = this.selectedOrderRevisionId.draft.toString();
            this.deleteButtonDrafts.openDeleteModal();
        }
    }
    // Order Grid Buttons
    else{
        if(args.item.id === 'tool-bar-accept-delivery-btn'){
            this.currentSelectedToolbarAction.isAcceptDelivery = true;
            if(!this.whichStatusesAllowAcceptDelivery.includes(this.currentOrderStatus)){
                this.ejDialogOrderTracker.show();
                return;
            }
            this.acceptDelivery('all');
        }
        else if(args.item.id === 'tool-bar-resubmit-order'){
            this.currentSelectedToolbarAction.isResubmitOrder = true;
            if(!this.whichStatusesAllowResubmit.includes(this.currentOrderStatus)){
                this.ejDialogOrderTracker.show();
                return;
            }
            this.resubmit('all');
        }
        else if(args.item.id === 'tool-bar-request-order-cancellation'){
            this.currentSelectedToolbarAction.isRequestOrderCancellation = true;
            if(!this.whichStatusesAllowCancel.includes(this.currentOrderStatus)){
                this.ejDialogOrderTracker.show();
                return;
            }
            this.cancel('all');
        }
    }
}

  tooltipValue(args) {

    let grid;

    if (args.target.scrollWidth <= args.target.clientWidth) {
      args.cancel = true;
    } else {

      switch (this.activeTab) {
        case 'AllOrders':
          grid = this.orderTrackerAllGrid;
          break;
        case 'DraftOrders':
          grid = this.orderTrackerDraftGrid;
          break;
      }

      const rowInfo = grid.getRowInfo(args.target);
      const rowData = rowInfo.rowData;
      this.tooltipObj.content = rowData[(rowInfo.column as any).field];
    }
  }

    ngAfterViewInit() {
      this.sub = this.toolTips.changes.subscribe((comps: QueryList<TooltipComponent>) => {
        this.tooltipObj = comps?.first;
      });
    }

    loadData() {
        if (this.storageManagerService.read('currentCustomer')) {
            this.pageState = this.orderService.PAGE_STATE_LOADING;
            this.sub = this.orderService.getOrderTrackerData(this.selectedDateRangeFilter).subscribe(untypedApiResult => {
                this.pageState = this.orderService.PAGE_STATE_READY;
                const apiResult: IApiTypedResult<any[]> = untypedApiResult as IApiTypedResult<any[]>;
                if (!apiResult || !apiResult.isSuccess) {
                    this.orderService.handleOrderError(this, apiResult, 'Failed to load order tracker data', false);
                } else {
                    // All orders (not draft)
                    this.orderTrackerAllGridData = apiResult.data.filter(x => !x.isDraftMode);
                    // Only draft orders
                    this.orderTrackerDraftGridData = apiResult.data.filter(x => x.isDraftMode);
                }
                this.pageState = this.orderService.PAGE_STATE_READY;
            },
                error => this.orderService.handleOrderError(this, error, 'Failed to load order tracker data', false)
            );
        }
    }

    // ------------------------------------------------------------
    // ENABLED STATES
    // ------------------------------------------------------------
    setToolbarState(whichGrid: string, orderStatus: string) {
        // "includes" is valid Ecmascript 2016 but VisualStudio is mad, hide it inside "any"
        this.canAcceptDelivery[whichGrid] = this.canOrderResubmit && (this.whichStatusesAllowAcceptDelivery as any).includes(orderStatus);
        this.canResubmit[whichGrid] = this.canOrderResubmit && (this.whichStatusesAllowResubmit as any).includes(orderStatus);
        this.canCancel[whichGrid] = this.canCancelOrder && (this.whichStatusesAllowCancel as any).includes(orderStatus);
        this.canDelete[whichGrid] = (this.whichStatusesAllowDelete as any).includes(orderStatus);
    }


    // ------------------------------------------------------------
    // GRIDS
    // ------------------------------------------------------------
    orderTrackerGridRowSelected(args: RowSelectEventArgs, whichGrid: string) {
        let selectedRowData = null;
        let orderStatus = null;
        this.selectedOrderRevisionId[whichGrid] = null;
        const selectedRecords: any[] = (whichGrid == 'all' ? this.orderTrackerAllGrid : this.orderTrackerDraftGrid).getSelectedRecords();
        if (selectedRecords) {
            selectedRowData = selectedRecords[0];
            orderStatus = selectedRowData.orderStatus;
            this.selectedOrderRevisionId[whichGrid] = selectedRowData.orderRevisionId;

            this.currentOrderStatus = orderStatus;
        }
        this.setToolbarState(whichGrid, orderStatus);
    }

    orderTrackerGridRowDeselected(args: RowDeselectEventArgs, whichGrid: string) {
        this.setToolbarState(whichGrid, null);
        this.selectedOrderRevisionId[whichGrid] = null;

        this.currentOrderStatus = '';
        this.resetCurrentSelectedToolbarAction();
    }

    resetCurrentSelectedToolbarAction(){
        for(let key in this.currentSelectedToolbarAction){
            this.currentSelectedToolbarAction[key] = false;
        }
    }

    // download sourceDoc
    downloadFile(filePath: string, fileName: string) {
        this.orderService.downloadFile(filePath, fileName).subscribe(response => {
            this.orderService.postDownloadFile(response, fileName);
        },
          error => {
            this.toastr.error(error.message);
          });
    }

    // ------------------------------------------------------------
    // BUTTONS
    // ------------------------------------------------------------

    // Accept delivery of order - change status to complete.
    acceptDelivery(whichGrid: string) {
        const orderRevisionId = this.selectedOrderRevisionId[whichGrid];
        if (this.ordersInProcess.acceptDelivery.includes(orderRevisionId)) {
            this.toastr.warning('Order is already in process.');
            return;
        }
        this.ordersInProcess.acceptDelivery.push(orderRevisionId);
        this.toastr.success('Order proccess started.');

        if (orderRevisionId && this.whichStatusesAllowAcceptDelivery.includes(this.currentOrderStatus)) {
            this.orderService.getOrderByOrderRevisionId(orderRevisionId)
                .pipe(catchError(this.orderService.handleOrderObjectError))
                .subscribe(
                    apiResult => {
                        const apiTypedResult = apiResult as IApiTypedResult<Order>;
                        if (apiTypedResult.isSuccess) {
                            // Successfully retrieved order
                            const order = apiTypedResult.data;
                            // Only proceed if status is valid for this operation
                            // "includes" is valid Ecmascript 2016 but VisualStudio is mad, hide it inside "any"
                            if ((this.whichStatusesAllowAcceptDelivery as any).includes(order.orderStatus)) {
                                // remember old values
                                order.beforeOrderStatus = order.orderStatus;
                                order.beforeConfirmedDate = order.confirmedDueDate;
                                // set new values
                                order.orderStatus = this.orderService.ORDER_STATUS_ORDER_COMPLETE;
                                const modifiedByUser = this.storageManagerService.read('userData').name;
                                // Save order
                                this.orderService.saveOrder(order, modifiedByUser)
                                    .pipe(catchError(this.orderService.handleOrderObjectError))
                                    .subscribe(apiResult => {
                                        // Inform user of result
                                        const apiTypedResult2: IApiTypedResult<Order> = apiResult as IApiTypedResult<Order>;
                                        if (apiTypedResult2.isSuccess) {
                                            this.loadData();
                                            this.orderService.alert(`Order`, `Accepted delivery of order.`);
                                        } else {
                                            this.orderService.alert('Order', `Failed to accept delivery of order.  ${apiTypedResult2.message}`);
                                        }
                                        this.ordersInProcess = this.ordersInProcess.acceptDelivery.filter(x => x !== orderRevisionId);
                                    },
                                    error => {
                                    this.toastr.error(error && error.errorMessage ? error.errorMessage : error);
                                    this.ordersInProcess = this.ordersInProcess.acceptDelivery.filter(x => x !== orderRevisionId);
                                });
                            } else {
                                this.orderService.alert('Order', `Cannot accept delivery of order in status ${order.orderStatus}`);
                                this.ordersInProcess = this.ordersInProcess.acceptDelivery.filter(x => x !== orderRevisionId);
                            }
                        } else {
                            this.orderService.alert('Order', `Failed to load order.  ${apiTypedResult.message}`);
                            this.ordersInProcess = this.ordersInProcess.acceptDelivery.filter(x => x !== orderRevisionId);
                        }
                    },
                    error => {
                        this.toastr.error(error.message);
                        this.ordersInProcess = this.ordersInProcess.acceptDelivery.filter(x => x !== orderRevisionId);
                    }
                );
        }
    }


    // Resubmit order - clone order, set clone status to draft, add comment
    resubmit(whichGrid: string) {
        // update the order status to Order Complete and save
        const orderRevisionId = this.selectedOrderRevisionId[whichGrid];
        if (this.ordersInProcess.resubmit.includes(orderRevisionId)) {
            this.toastr.warning('Order is already in process.');
            return;
        }
        this.ordersInProcess.resubmit.push(orderRevisionId);
        this.toastr.success('Order proccess started.');

        if (orderRevisionId && this.whichStatusesAllowResubmit.includes(this.currentOrderStatus)) {
            this.orderService.getOrderByOrderRevisionId(orderRevisionId)
                // If there's an error, force it into {isSuccess: false, message: <some message>, data: null} format
                .pipe(catchError(this.orderService.handleOrderObjectError))
                .subscribe(apiResult => {
                    const apiTypedResult = apiResult as IApiTypedResult<Order>;
                    if (apiTypedResult.isSuccess) {
                        // Successfully retrieved order
                        const order = apiTypedResult.data;
                        // Only proceed if status is valid for this operation
                        // "includes" is valid Ecmascript 2016 but VisualStudio is mad, hide it inside "any"
                        if ((this.whichStatusesAllowResubmit as any).includes(order.orderStatus)) {
                            const modifiedByUser = this.storageManagerService.read('userData').name;
                            const modifiedByUserGuid = this.storageManagerService.read('userData').userguid;
                            const customerGuid = this.settingsService.getCustomerGuid();

                            // Save modify user as order intitiator.
                            // Need the full list of employees so we can get all the stuff
                            // we need to populate OrderUser fields.
                            this.orderService.getEmployees(customerGuid)
                                .subscribe(allEmployeesInCompany => {
                                    const initiators: OrderUser[] = this.orderService.userGuidsToOrderUsers(
                                        allEmployeesInCompany, [modifiedByUserGuid], this.orderService.ORDER_USER_TYPE_ORDER_INITIATOR);
                                    order.orderUsers.push(initiators[0]);

                                    // Save order
                                    this.orderService.cloneOrder(order, modifiedByUser)
                                        .pipe(catchError(this.orderService.handleOrderObjectError))
                                        .subscribe(apiResult => {
                                            // Inform user of result
                                            const apiTypedResult2: IApiResult = apiResult as IApiResult;
                                            if (apiTypedResult2.isSuccess) {
                                                this.loadData();
                                                this.orderService.alert(`Order`, this.ORDER_RESUBMIT_MESSAGE);
                                            } else {
                                                this.orderService.alert('Order', `Failed to resubmit order.  ${apiTypedResult2.message}`);
                                            }
                                            this.ordersInProcess = this.ordersInProcess.resubmit.filter(x => x !== orderRevisionId);
                                        },
                                        error => {
                                            this.toastr.error(error.message);
                                            this.ordersInProcess = this.ordersInProcess.resubmit.filter(x => x !== orderRevisionId);
                                        });
                                },
                                  error => {
                                    this.toastr.error(error.message);
                                    this.ordersInProcess = this.ordersInProcess.resubmit.filter(x => x !== orderRevisionId);
                                });
                        } else {
                            this.orderService.alert('Order', `Cannot resubmit order in status ${order.orderStatus}`);
                            this.ordersInProcess = this.ordersInProcess.resubmit.filter(x => x !== orderRevisionId);
                        }
                    } else {
                        this.orderService.alert('Order', `Failed to resubmit order.  ${apiTypedResult.message}`);
                        this.ordersInProcess = this.ordersInProcess.resubmit.filter(x => x !== orderRevisionId);
                    }
                },
                error => {
                    this.toastr.error(error.message);
                    this.ordersInProcess = this.ordersInProcess.resubmit.filter(x => x !== orderRevisionId);
                });
        }
    }

    // Cancel order - change status to canceled
    cancel(whichGrid: string) {
        const orderRevisionId = this.selectedOrderRevisionId[whichGrid];
        if (this.ordersInProcess.cancel.includes(orderRevisionId)) {
            this.toastr.warning('Order is already in process.');
            return;
        }
        this.ordersInProcess.cancel.push(orderRevisionId);
        this.toastr.success('Order proccess started.');

        if (orderRevisionId && this.whichStatusesAllowCancel.includes(this.currentOrderStatus)) {
            this.orderService.getOrderByOrderRevisionId(orderRevisionId)
                // If there's an error, force it into {isSuccess: false, message: <some message>, data: null} format
                .pipe(catchError(this.orderService.handleOrderObjectError))
                .subscribe(apiResult => {
                    const apiTypedResult = apiResult as IApiTypedResult<Order>;
                    if (apiTypedResult.isSuccess) {
                        // Successfully retrieved order
                        const order = apiTypedResult.data;
                        // Only proceed if status is valid for this operation
                        // "includes" is valid Ecmascript 2016 but VisualStudio is mad, hide it inside "any"
                        if ((this.whichStatusesAllowCancel as any).includes(order.orderStatus)) {
                            // remember old values
                            order.beforeConfirmedDate = order.confirmedDueDate;
                            const modifiedByUser = this.storageManagerService.read('userData').name;
                            // Save order
                            this.orderService.requestOrderCancellation(true, order, modifiedByUser, '/order-tracker', true);
                        } else {
                            this.orderService.alert('Order', `Cannot cancel order in status ${order.orderStatus}`);
                        }
                        this.ordersInProcess = this.ordersInProcess.cancel.filter(x => x !== orderRevisionId);
                    } else {
                        this.orderService.alert('Order', `Failed to load order.  ${apiTypedResult.message}`);
                        this.ordersInProcess = this.ordersInProcess.cancel.filter(x => x !== orderRevisionId);
                    }
                },
                error => {
                    this.toastr.error(error.message);
                    this.ordersInProcess = this.ordersInProcess.cancel.filter(x => x !== orderRevisionId);
                });
        }
    }

    // Change grid filter operator from "startwith" to "contains" for columns that are string
    public begin(args: any): void {
        if (args.requestType === 'filtering') {
            if (args.columns) {
                args.columns.forEach(col => {
                    switch (args.currentFilteringColumn) {
                        case 'confirmedDueDate':
                        case 'actualDeliveryDate':
                        case 'createdOn':
                        case 'modifiedOn':
                            break;

                        default:
                            col.operator = 'contains';       // set the “contains” operator for the column
                            break;
                    }
                });
            }
        }
    }

    dateRangeSelectionChanged(dateRangeLimitValue: string) {
        this.selectedDateRangeFilter = dateRangeLimitValue;
        this.loadData();
    }

    rowSelecting(e) {
        e.cancel = e.isHeaderCheckboxClicked;

        const currentGrid = this.activeTab === 'AllOrders' ? this.orderTrackerAllGrid : this.orderTrackerDraftGrid;
        if (currentGrid.getSelectedRecords().length) {
            currentGrid.clearSelection();
        }
    }

    ngOnDestroy() {
      if (this.sub) {
        this.sub.unsubscribe();
      }
    }

}
